import { useLayoutEffect, useMemo } from "react";
import {
  CSS_VAR_MAP,
  resolveTheme,
  THEME_KEYS,
  type ThemePalette,
} from "./palette";

export type ThemeSource =
  Partial<ThemePalette> | Record<string, unknown> | null | undefined;

function themeSignature(source: ThemeSource): string {
  const palette = resolveTheme(source);
  return THEME_KEYS.map((key) => palette[key]).join("|");
}

/** The only runtime writer for company palette CSS variables. */
export function applyCompanyTheme(
  source: ThemeSource,
  root: HTMLElement = document.documentElement,
): ThemePalette {
  const palette = resolveTheme(source);
  for (const key of Object.keys(CSS_VAR_MAP) as (keyof ThemePalette)[]) {
    root.style.setProperty(CSS_VAR_MAP[key], palette[key]);
  }
  // Chrome aliases: Navbar + Sidebar share `primary` + `onBrand` so website
  // and dashboard stay visually aligned without extra theme keys.
  root.style.setProperty("--brand-header", palette.primary);
  root.style.setProperty("--brand-sidebar", palette.primary);
  root.style.setProperty("--brand-content", palette.pageBackground);
  root.style.setProperty("--brand-on-chrome", palette.onBrand);
  return palette;
}

export function useApplyCompanyTheme(source: ThemeSource): ThemePalette {
  const signature = themeSignature(source);
  const palette = useMemo(
    () => resolveTheme(source),
    // signature captures the resolved palette identity for this source
    // eslint-disable-next-line react-hooks/exhaustive-deps -- source objects are compared via signature
    [signature],
  );

  useLayoutEffect(() => {
    applyCompanyTheme(palette);
  }, [palette]);

  return palette;
}
